// Signup.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useTranslation } from "react-i18next";

// ✅ message_key only
const translateBackend = (t, data) => {
  if (!data) return t("msg_server_or_network");
  if (data.message_key) return t(data.message_key, data.message_params || {});
  return t("msg_unknown_error");
};

const Signup = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const authMotionClass =
    location.state?.authDirection === "toSignup" ? "auth-slide-from-right" : "auth-slide-soft";

  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [application, setApplication] = useState(""); // ✅ added
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success"); // "success" | "error"
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const strongPwd = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setFieldErrors({});
    setMessageType("success");

    const errs = {};

    if (!email) errs.email = t("signup_err_email_required");
    else if (!emailRegex.test(email)) errs.email = t("msg_invalid_email");

    if (!city) errs.city = t("signup_err_city_required");
    if (!country) errs.country = t("signup_err_country_required");
    if (!application) errs.application = t("signup_err_application_required"); 

    if (!password) errs.password = t("signup_err_password_required");
    else if (!strongPwd.test(password)) errs.password = t("signup_err_password_weak");

    if (!confirmPassword) errs.confirm_password = t("signup_err_confirm_required");
    else if (password !== confirmPassword) {
      errs.confirm_password = t("signup_err_password_mismatch");
    }

    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setMessage(t("signup_fix_errors"));
      setMessageType("error");
      return;
    }

    const lang =
      (i18n.language || localStorage.getItem("i18nextLng") || "fr")
        .toString()
        .split("-")[0];

    setLoading(true);

    try {
      console.log("i18n.language =", i18n.language);
      console.log("localStorage i18nextLng =", localStorage.getItem("i18nextLng"));
      console.log("lang sent =", lang);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/signup`,
        {
          email: email.trim(),
          city: city.trim(),
          country: country.trim(),
          application: application.trim(), 
          password,
          confirm_password: confirmPassword,
        },
        {
          headers: {
            "Accept-Language": lang,
          },
        }
      );

      setMessage(translateBackend(t, res.data));
      setMessageType("success");

      setPassword("");
      setConfirmPassword("");
      setApplication(""); 
    } catch (err) {
      const data = err?.response?.data;

      if (err?.response?.status === 400 && Array.isArray(data?.errors)) {
        const be = {};
        data.errors.forEach((e) => {
          if (!e?.field) return;
          if (e?.message_key) {
            be[e.field] = t(e.message_key, e.message_params || {});
          } else {
            be[e.field] = t("signup_err_invalid_value");
          }
        });
        setFieldErrors(be);

        setMessage(translateBackend(t, data));
        setMessageType("error");
      } else {
        setMessage(translateBackend(t, data));
        setMessageType("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const isError = messageType === "error";
  const messageClass = isError ? "message message--error" : "message message--success";

  return (
    <div className={`auth-shell auth-shell-signup ${authMotionClass}`}>
      <section className="auth-visual-panel auth-switch-panel">
        <div className="auth-brand-block">
          <span className="auth-brand-mark">A</span>
          <p>{t("assist_by_scan", "ASSIST BY SCAN")}</p>
        </div>

        <div className="auth-orbit" aria-hidden="true">
          <div className="auth-orbit-card auth-orbit-card-main">
            <span className="auth-chip">APP</span>
            <strong>{t("auth_signup_feature_1", "QR codes par équipement")}</strong>
            <small>{t("auth_signup_story_text", "Lancez votre application, invitez votre équipe et connectez vos équipements grâce aux QR codes intelligents.")}</small>
          </div>
          <div className="auth-orbit-card auth-orbit-card-small auth-orbit-card-top">
            <strong>PDF</strong>
            <small>{t("auth_signup_feature_3", "Rapports PDF centralisés")}</small>
          </div>
          <div className="auth-orbit-card auth-orbit-card-small auth-orbit-card-bottom">
            <strong>FORM</strong>
            <small>{t("auth_signup_feature_2", "Formulaires dynamiques")}</small>
          </div>
        </div>

        <div className="auth-visual-copy">
          <p className="auth-eyebrow">{t("signup_title")}</p>
          <h1>{t("auth_signup_story_title", "Créez votre espace maintenance en quelques minutes.")}</h1>
        </div>
      </section>

      <section className="auth-workspace-panel auth-switch-card">
        <div className="auth-workspace-card">
          <div className="auth-form-head">
            <span>{t("assist_by_scan", "ASSIST BY SCAN")}</span>
            <strong>{t("signup_title")}</strong>
          </div>

          <h2 className="title">{t("signup_title")}</h2>

          {message && <p className={messageClass}>{message}</p>}

          <form onSubmit={handleSubmit} className="form" noValidate>
            <input
              type="email"
              placeholder={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              aria-invalid={!!fieldErrors.email}
              required
            />
            {fieldErrors.email && <p className="error-text">{fieldErrors.email}</p>}

            <div className="auth-form-grid">
              <div>
                <input
                  type="text"
                  placeholder={t("city")}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="input"
                  aria-invalid={!!fieldErrors.city}
                  required
                />
                {fieldErrors.city && <p className="error-text">{fieldErrors.city}</p>}
              </div>

              <div>
                <input
                  type="text"
                  placeholder={t("country")}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="input"
                  aria-invalid={!!fieldErrors.country}
                  required
                />
                {fieldErrors.country && <p className="error-text">{fieldErrors.country}</p>}
              </div>
            </div>

            <input
              type="text"
              placeholder={t("application")}
              value={application}
              onChange={(e) => setApplication(e.target.value)}
              className="input"
              aria-invalid={!!fieldErrors.application}
              required
            />
            {fieldErrors.application && (
              <p className="error-text">{fieldErrors.application}</p>
            )}

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input input--with-eye"
                aria-invalid={!!fieldErrors.password}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="eye-btn"
                aria-label={showPassword ? t("hide_password") : t("show_password")}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {fieldErrors.password && <p className="error-text">{fieldErrors.password}</p>}

            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder={t("signup_confirm_password")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input input--with-eye"
                aria-invalid={!!fieldErrors.confirm_password}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((s) => !s)}
                className="eye-btn"
                aria-label={showConfirmPassword ? t("hide_password") : t("show_password")}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {fieldErrors.confirm_password && (
              <p className="error-text">{fieldErrors.confirm_password}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`btn btn-lg ${loading ? "btn--muted" : ""}`}
            >
              {loading ? t("signup_loading") : t("signup")}
            </button>
          </form>

          <p className="auth-bottom-link">
            {t("signup_have_account")}{" "}
            <Link to="/login" state={{ authDirection: "toLogin" }}>
              {t("nav_login")}
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Signup;
